export interface PickerDoc {
    id: string;
    name: string;
    mimeType?: string;
    sizeBytes?: number;
}

export interface OpenGoogleDrivePickerDialogOptions {
    accessToken: string;
    appId?: string;
    developerKey?: string;
    title?: string;
    multiselect?: boolean;
}

export interface OpenGoogleDrivePickerDialogResult {
    action: "picked" | "cancel";
    docs: PickerDoc[];
}

declare global {
    interface Window {
        gapi?: {
            load: (name: string, callback: () => void) => void;
        };
        google?: {
            picker?: {
                Action: { PICKED: string; CANCEL: string };
                Document: { ID: string; NAME: string; MIME_TYPE: string; SIZE_BYTES: string };
                Response: { ACTION: string; DOCUMENTS: string };
                Feature: { MULTISELECT_ENABLED: unknown };
                ViewId: { DOCS: unknown };
                DocsView: new (viewId: unknown) => {
                    setIncludeFolders: (value: boolean) => void;
                    setSelectFolderEnabled: (value: boolean) => void;
                };
                PickerBuilder: new () => {
                    addView: (view: unknown) => any;
                    setOAuthToken: (token: string) => any;
                    setTitle: (title: string) => any;
                    setCallback: (callback: (data: Record<string, any>) => void) => any;
                    enableFeature: (feature: unknown) => any;
                    setDeveloperKey: (key: string) => any;
                    setAppId: (appId: string) => any;
                    build: () => { setVisible: (visible: boolean) => void };
                };
            };
        };
    }
}

let scriptLoadPromise: Promise<void> | null = null;
let pickerApiLoadPromise: Promise<void> | null = null;

async function loadGoogleApiScript(): Promise<void> {
    if (window.gapi) return;
    if (scriptLoadPromise) return scriptLoadPromise;

    scriptLoadPromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>('script[data-google-api="picker"]');
        if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error("Failed to load Google API script.")), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = "https://apis.google.com/js/api.js";
        script.async = true;
        script.defer = true;
        script.dataset.googleApi = "picker";
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Google API script."));
        document.head.appendChild(script);
    });

    return scriptLoadPromise;
}

async function loadPickerApi(): Promise<void> {
    if (window.google?.picker) return;
    if (pickerApiLoadPromise) return pickerApiLoadPromise;

    pickerApiLoadPromise = (async () => {
        await loadGoogleApiScript();
        if (!window.gapi) {
            throw new Error("Google API client is unavailable.");
        }
        await new Promise<void>(resolve => {
            window.gapi?.load("picker", () => resolve());
        });
        if (!window.google?.picker) {
            throw new Error("Google Picker API did not initialize.");
        }
    })();

    return pickerApiLoadPromise;
}

export async function openGoogleDrivePickerDialog(
    options: OpenGoogleDrivePickerDialogOptions
): Promise<OpenGoogleDrivePickerDialogResult> {
    await loadPickerApi();

    const picker = window.google?.picker;
    if (!picker) {
        throw new Error("Google Picker API is unavailable.");
    }

    return new Promise<OpenGoogleDrivePickerDialogResult>((resolve, reject) => {
        const docsView = new picker.DocsView(picker.ViewId.DOCS);
        docsView.setIncludeFolders(false);
        docsView.setSelectFolderEnabled(false);

        const builder = new picker.PickerBuilder()
            .addView(docsView)
            .setOAuthToken(options.accessToken)
            .setTitle(options.title || "Select files from Google Drive")
            .setCallback((data: Record<string, any>) => {
                const action = data[picker.Response.ACTION];
                if (action === picker.Action.CANCEL) {
                    resolve({ action: "cancel", docs: [] });
                    return;
                }
                if (action !== picker.Action.PICKED) {
                    return;
                }

                const docs = (data[picker.Response.DOCUMENTS] || []).map((doc: Record<string, any>) => ({
                    id: String(doc[picker.Document.ID] || ""),
                    name: String(doc[picker.Document.NAME] || ""),
                    mimeType: doc[picker.Document.MIME_TYPE] ? String(doc[picker.Document.MIME_TYPE]) : undefined,
                    sizeBytes: typeof doc[picker.Document.SIZE_BYTES] === "number"
                        ? doc[picker.Document.SIZE_BYTES]
                        : undefined,
                })).filter((doc: PickerDoc) => Boolean(doc.id));

                resolve({ action: "picked", docs });
            });

        if (options.multiselect !== false) {
            builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
        }
        if (options.developerKey) {
            builder.setDeveloperKey(options.developerKey);
        }
        if (options.appId) {
            builder.setAppId(options.appId);
        }

        try {
            builder.build().setVisible(true);
        } catch (error) {
            reject(error instanceof Error ? error : new Error("Failed to open Google Picker."));
        }
    });
}
